'use client';

import {
  Card,
  CardContent,
  CardHeader,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useState, type SyntheticEvent } from 'react';

const PANEL_WIDTH = '308px';
const HEADER_PADDING = '12px 24px';

/**
 * A toolbar toggle: filled while its panel is open, and on hover it lifts to the
 * pale hover background with a primary-coloured icon, so an open panel still has
 * a visible affordance for closing.
 */
export const toolbarToggleButtonStyle = (isOpen: boolean) => ({
  backgroundColor: isOpen ? 'var(--mui-palette-primary-main)' : undefined,
  color: isOpen
    ? 'var(--mui-palette-background-default)'
    : 'var(--mui-palette-text-primary)',
  '&:hover': {
    backgroundColor: 'var(--mui-palette-primary-hover)',
    color: 'var(--mui-palette-primary-main)',
  },
});

// The self-referencing selector is deliberate: the theme paints card-header
// actions with `.MuiCardHeader-action .MuiIconButton-root`, which outranks a
// plain one-class `sx` rule.
const panelCloseButtonStyle = {
  '&.MuiIconButton-root': {
    color: 'var(--mui-palette-primary-main)',
  },
};

/**
 * These buttons sit at the table's right edge, where a tooltip wider than the
 * button overruns the viewport — and the theme's flip fallbacks answer that by
 * swinging it to the button's side, so "Inventory settings" lands to the left of
 * the button rather than above it. Dropping the fallbacks keeps the tooltip above
 * and leaves the overflow to preventOverflow, which slides it into view instead.
 */
const KEEP_TOOLTIP_ABOVE = {
  popper: {
    modifiers: [
      { name: 'flip', options: { fallbackPlacements: [] } },
      // Without padding the slid tooltip ends up flush against the window edge.
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  },
};

/** A table toolbar's icon button, filled while the panel it opens is open. */
const ToolbarPanelButton = ({
  icon,
  tooltip,
  testId,
  isOpen,
  onClick,
  buttonRef,
}: {
  icon: string;
  tooltip: string;
  testId: string;
  isOpen: boolean;
  onClick: () => void;
  buttonRef?: (element: HTMLElement | null) => void;
}) => (
  <Tooltip title={tooltip} placement='top' slotProps={KEEP_TOOLTIP_ABOVE}>
    <IconButton
      data-testid={testId}
      ref={buttonRef}
      onClick={onClick}
      sx={toolbarToggleButtonStyle(isOpen)}
    >
      <i className={icon} />
    </IconButton>
  </Tooltip>
);

/**
 * A toolbar button and the panel it opens: the shared chrome behind Manage
 * Columns and Table settings, so the two cannot drift apart in width, corner,
 * shadow, header or dismissal behaviour. Callers supply only the contents.
 */
export const ToolbarPanel = ({
  icon,
  title,
  tooltip = title,
  testId,
  contentSx,
  footer,
  children,
}: {
  icon: string;
  title: string;
  /** Defaults to the panel's title, which is what the button announces. */
  tooltip?: string;
  testId: string;
  contentSx?: SxProps<Theme>;
  /**
   * An action row below the contents, outside the scroll area. It is handed the
   * panel's own dismissal so a footer action can close the panel.
   */
  footer?: (closePanel: () => void) => React.ReactNode;
  /**
   * The panel's contents. They never dismiss it: a visit here makes as many picks
   * as the user wants, and the header's close, the footer and a click away are
   * what end it.
   */
  children: React.ReactNode;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const closePanel = () => setOpen(false);

  const closeOnClickAway = (event: Event | SyntheticEvent) => {
    const target = event.target instanceof HTMLElement ? event.target : null;

    // A control inside the panel may portal its own menu out of the card — the
    // page-size select does — and those clicks must not take the panel with them.
    if (anchorEl?.contains(target) || target?.closest('.MuiPopover-root')) {
      return;
    }

    closePanel();
  };

  return (
    <>
      <ToolbarPanelButton
        icon={icon}
        tooltip={tooltip}
        testId={testId}
        isOpen={open}
        onClick={() => setOpen(!open)}
        buttonRef={setAnchorEl}
      />
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='bottom-end'
        transition
        sx={{ zIndex: 'modal' }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              sx={{
                borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                boxShadow: 'var(--mui-customShadows-lg)',
              }}
            >
              {/* Mousedown, not click: a click that starts on a control in the
                  panel and ends on its menu's backdrop reports `body` as its
                  target, which no target check can attribute to the panel. */}
              <ClickAwayListener
                mouseEvent='onMouseDown'
                onClickAway={closeOnClickAway}
              >
                <Card
                  sx={{
                    borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                    boxShadow: 'var(--mui-customShadows-panelMd)',
                    width: PANEL_WIDTH,
                  }}
                >
                  <CardHeader
                    action={
                      <Tooltip title='Close'>
                        <IconButton
                          onClick={closePanel}
                          sx={panelCloseButtonStyle}
                        >
                          <i className='material-symbols-close-rounded' />
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{ padding: HEADER_PADDING }}
                    title={<Typography variant='h3'>{title}</Typography>}
                  />
                  <CardContent sx={contentSx}>{children}</CardContent>
                  {footer?.(closePanel)}
                </Card>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
